import React, { FC, useState } from 'react';
import { RouteComponentProps } from 'react-router-dom';
import { Alert, Button, Card, Space, Typography } from 'antd';
import { Trans, useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { useAcceptInvitation, useInvitationPageState } from '~/hooks/invitation';
import { useIdentity } from '~/hooks/identity';
import { auth } from '~/api/axios';
import { getProfiles } from '~/api/onboarding';
import { useDispatchResetReduxState } from '~/pages/login';

const { Title, Text } = Typography;

const Invitation: FC<RouteComponentProps> = (props) => {
  const { history } = props;

  const [alreadyInvited, setAlreadyInvited] = useState(false);

  const [pageState] = useInvitationPageState();
  const apiIdentityQuery = useIdentity();
  const businessName = pageState.data?.businessLegalName;
  const invitationEmail = pageState.data?.emailAddress;
  const identityEmail = apiIdentityQuery.data?.emailAddress;

  const goToMainPage = () => {
    history.push('/');
  };
  const resetState = useDispatchResetReduxState();
  const handleCancel = () => {
    auth.deleteToken();
    resetState();
  };

  const acceptInvitationMutation = useAcceptInvitation();
  const handleAccept = async () => {
    try {
      await acceptInvitationMutation.mutateAsync();
      await apiIdentityQuery.refetch();
      const { data } = await getProfiles();
      const businessProfile = data.find((item) => item.type === 'BUSINESS');
      if (businessProfile?.id && businessProfile?.solutionId) {
        auth.setProfile(businessProfile.id);
        auth.setSolution(businessProfile.solutionId);
      }
      history.replace('/');
    } catch (err: any) {
      const error: any = err;
      const code = error?.response?.data?.code;
      if (code === 'RESOURCE_ALREADY_EXISTS') {
        setAlreadyInvited(true);
      }
    }
  };

  const { t } = useTranslation('invitation');

  return (
    <Card className="Invitation-Card">
      <Title className="Invitation-Title" level={2}>
        {t('translation:businessPortal').toUpperCase()}
      </Title>
      {alreadyInvited ? (
        <>
          <Alert
            className="Invitation-TextSignIn"
            type="error"
            message={
              <Trans
                ns="invitation"
                defaults="Your profile <strong>{{identityEmail}}</strong> is already linked to the company <strong>{{businessName}}</strong>. Please continue as <strong>{{identityEmail}}</strong> or enter another phone number to use this invitation link"
                values={{
                  businessName,
                  identityEmail,
                }}
              />
            }
          />
          <Space direction="vertical" style={fullWidth}>
            <MultilineButton onClick={goToMainPage} type="primary" size="large">
              <Trans
                ns="invitation"
                defaults="Yes, I want to continue as <strong>{{identityEmail}}</strong>"
                values={{
                  identityEmail,
                }}
              />
            </MultilineButton>
            <MultilineButton onClick={handleCancel} size="large">
              <Trans
                ns="invitation"
                defaults="No, I will enter another phone number and use <strong>{{invitationEmail}}</strong>"
                values={{
                  invitationEmail,
                }}
              />
            </MultilineButton>
          </Space>
        </>
      ) : (
        <>
          <Text type="secondary" className="Invitation-TextSignIn">
            <Trans
              ns="invitation"
              defaults="Welcome to <strong>{{businessName}}</strong> company! Do you confirm enrolment to <strong>{{businessName}}</strong> company as <strong>{{email}}</strong>?"
              values={{
                businessName,
                email: identityEmail,
              }}
            />
          </Text>
          <Space direction="vertical" style={fullWidth}>
            <Button
              onClick={handleAccept}
              loading={acceptInvitationMutation.isLoading}
              type="primary"
              size="large"
              style={fullWidth}
            >
              {t('Yes, continue enrolment')}
            </Button>
            <Button onClick={goToMainPage} size="large" style={fullWidth}>
              {t("No, I'll enroll later")}
            </Button>
          </Space>
        </>
      )}
    </Card>
  );
};

const MultilineButton = styled(Button)`
  width: 100%;
  white-space: break-spaces;
  height: auto;
`;

const fullWidth = { width: '100%' };

export default Invitation;
